import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import { useDiagnosisMutations } from '@/features/patient-record/hooks/useDiagnosisMutations';
import { patientQueries } from '@/features/patient-record/queries';
import {
  diagnosisStatusToBadgeVariant,
  diagnosisStatusToI18nKey,
} from '@/lib/clinicalBadges';
import type {
  DiagnosisStatus,
  DiagnosisViewModel,
} from '@/features/patient-record/types';

import { AddDiagnosisDialog } from './AddDiagnosisDialog';
import { ClinicalSection, type ClinicalSectionLabels } from './ClinicalSection';
import { ConfirmDeleteDialog } from './_shared/ConfirmDeleteDialog';
import { useFocusOnMount } from './_shared/useFocusOnMount';

const DIAGNOSIS_STATUSES: readonly DiagnosisStatus[] = [
  'Confirmed',
  'Suspected',
  'Ruled Out',
  'Molecular Diagnosis',
];

interface DiagnosesSectionProps {
  readonly guid: string;
}

/**
 * Vocabulary-term diagnoses (OMIM, Orphanet) for a patient. New diagnoses are
 * added via `AddDiagnosisDialog` (vocabulary + term picker, status/age/notes); the section
 * itself supports inline edit (status / age at diagnosis / notes) and
 * delete. The term is fixed once recorded — wrong term ⇒ delete and
 * re-add.
 */
export function DiagnosesSection({ guid }: DiagnosesSectionProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery({ ...patientQueries.diagnoses(guid), placeholderData: keepPreviousData });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DiagnosisViewModel | null>(null);
  const { remove } = useDiagnosisMutations(guid);

  const labels: ClinicalSectionLabels = {
    title: t('patient.diagnoses.heading'),
    empty: t('patient.diagnoses.empty'),
    loading: t('patient.diagnoses.loading'),
    errorHeading: t('patient.diagnoses.error.heading'),
    errorMessage: t('patient.diagnoses.error.message'),
    codeLabel: t('routes.patient.error.codeLabel'),
    retryLabel: t('routes.patient.error.retry'),
  };

  return (
    <>
      <ClinicalSection
        id="diagnoses"
        labels={labels}
        query={query}
        summary={(query.data ?? []).map((d) => {
          const status = t(`patient.diagnoses.status.${diagnosisStatusToI18nKey(d.status)}`);
          const value =
            d.ageAtDiagnosis !== null
              ? `${status} · ${t('patient.diagnoses.fields.ageAtDiagnosisShort', { value: d.ageAtDiagnosis })}`
              : status;
          return { label: d.title, value, filled: true };
        })}
        itemKey={(diagnosis) => diagnosis.id}
        renderItem={(diagnosis) =>
          editingId === diagnosis.id ? (
            <DiagnosisEditor
              guid={guid}
              diagnosis={diagnosis}
              onCancel={() => {
                setEditingId(null);
              }}
            />
          ) : (
            <DiagnosisRow
              diagnosis={diagnosis}
              onEdit={() => {
                setEditingId(diagnosis.id);
              }}
              onDelete={() => {
                setPendingDelete(diagnosis);
              }}
            />
          )
        }
        headerAction={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {t('patient.diagnoses.add.button')}
          </Button>
        }
      />
      {creating && (
        <AddDiagnosisDialog
          guid={guid}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('patient.diagnoses.confirmDeleteTitle')}
          message={t('patient.diagnoses.confirmDeleteMessage', {
            title: pendingDelete.title,
          })}
          confirmLabel={t('patient.diagnoses.delete')}
          busy={remove.isPending}
          onCancel={() => {
            setPendingDelete(null);
          }}
          onConfirm={() => {
            remove.mutate(pendingDelete.id, {
              onSettled: () => {
                setPendingDelete(null);
              },
            });
          }}
        />
      )}
    </>
  );
}

interface DiagnosisRowProps {
  readonly diagnosis: DiagnosisViewModel;
  readonly onEdit: () => void;
  readonly onDelete: () => void;
}

function DiagnosisRow({ diagnosis, onEdit, onDelete }: DiagnosisRowProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-2 group">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {diagnosis.termId}
          </Badge>
          <span className="text-sm font-medium text-card-foreground">
            {diagnosis.title}
          </span>
          <Badge
            variant={diagnosisStatusToBadgeVariant(diagnosis.status)}
            className="ml-auto"
          >
            {t(`patient.diagnoses.status.${diagnosisStatusToI18nKey(diagnosis.status)}`)}
          </Badge>
        </div>
        {(diagnosis.ageAtDiagnosis !== null || diagnosis.notes !== null) && (
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
            {diagnosis.ageAtDiagnosis !== null && (
              <span>
                {t('patient.diagnoses.fields.ageAtDiagnosis', {
                  value: diagnosis.ageAtDiagnosis,
                })}
              </span>
            )}
            {diagnosis.notes !== null && <span>{diagnosis.notes}</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          aria-label={t('patient.diagnoses.edit')}
          className="h-7 w-7 p-0"
        >
          <Pencil className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          aria-label={t('patient.diagnoses.delete')}
          className="h-7 w-7 p-0"
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

interface DiagnosisEditorProps {
  readonly guid: string;
  readonly diagnosis: DiagnosisViewModel;
  readonly onCancel: () => void;
}

interface DiagnosisDraft {
  readonly status: DiagnosisStatus;
  readonly ageAtDiagnosis: string;
  readonly notes: string;
}

const toDraft = (diagnosis: DiagnosisViewModel): DiagnosisDraft => ({
  status: diagnosis.status,
  ageAtDiagnosis: diagnosis.ageAtDiagnosis ?? '',
  notes: diagnosis.notes ?? '',
});

function DiagnosisEditor({
  guid,
  diagnosis,
  onCancel,
}: DiagnosisEditorProps): ReactElement {
  const { t } = useTranslation();
  const { update } = useDiagnosisMutations(guid);
  const ageRef = useFocusOnMount<HTMLInputElement>();

  const toInput = (d: DiagnosisDraft) => ({
    id: diagnosis.id,
    termId: diagnosis.termId,
    vocabulary: diagnosis.vocabulary,
    title: diagnosis.title,
    status: d.status,
    ageAtDiagnosis: d.ageAtDiagnosis.trim().length > 0 ? d.ageAtDiagnosis.trim() : null,
    notes: d.notes.trim().length > 0 ? d.notes.trim() : null,
  });

  // PTV2-1327: the primitive owns the draft. This is an edit-only inline editor, so it autosaves in
  // place (the commit does NOT collapse the editor); only the explicit Save button closes it. It
  // registers while dirty so clicking away in the pedigree node editor (or leaving the section on
  // the patient record) prompts instead of silently dropping the edit.
  const form = useEditableDraft<DiagnosisDraft>({
    initial: toDraft(diagnosis),
    label: t('patient.diagnoses.editFormLabel', { defaultValue: 'Diagnosis' }),
    onSave: (d) => update.mutateAsync(toInput(d)),
  });
  const { draft } = form;

  // The explicit Save button: commit AND close.
  const submitAndClose = (): void => {
    void update.mutateAsync(toInput(draft)).then(onCancel).catch(() => undefined);
  };

  return (
    <form
      ref={form.containerProps.ref}
      className="space-y-2 rounded-md border border-input bg-background p-3"
      onSubmit={(event) => {
        event.preventDefault();
        submitAndClose();
      }}
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape') onCancel();
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">
          {diagnosis.termId}
        </Badge>
        <span className="text-sm font-medium text-card-foreground flex-1 min-w-0 truncate">
          {diagnosis.title}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="space-y-1 text-xs font-medium">
          {t('patient.diagnoses.fields.status')}
          <Select
            value={draft.status}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, status: event.target.value as DiagnosisStatus }));
            }}
            className="h-8 text-sm"
          >
            {DIAGNOSIS_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`patient.diagnoses.status.${diagnosisStatusToI18nKey(status)}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="space-y-1 text-xs font-medium">
          {t('patient.diagnoses.fields.ageAtDiagnosisLabel')}
          <Input
            ref={ageRef}
            value={draft.ageAtDiagnosis}
            placeholder={t('patient.diagnoses.fields.agePlaceholder')}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, ageAtDiagnosis: event.target.value }));
            }}
            className="h-8 text-sm"
          />
        </label>
        <label className="space-y-1 text-xs font-medium">
          {t('patient.diagnoses.fields.notesLabel')}
          <Input
            value={draft.notes}
            placeholder={t('patient.diagnoses.fields.notesPlaceholder')}
            onChange={(event) => {
              form.setDraft((d) => ({ ...d, notes: event.target.value }));
            }}
            className="h-8 text-sm"
          />
        </label>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={update.isPending}
        >
          {t('ui.dialog.close')}
        </Button>
        <Button
          type="submit"
          size="sm"
          loading={update.isPending}
          loadingText={t('patient.dirtyFooter.saving')}
        >
          {t('patient.diagnoses.save')}
        </Button>
      </div>
    </form>
  );
}
